import { Brand, FinanceOffer, Vehicle } from '../types/catalog';
import { isExpired } from './format';
import { CircularQuote, bestCircularQuote, circularToOffer, getCircularFor } from './circular';

export interface OfferContext {
  offer: FinanceOffer;
  source: 'unidad' | 'marca';
  /** Presente cuando la condición sale de la circular oficial de la marca. */
  circular?: CircularQuote;
}

export interface ScheduleRow {
  n: number;
  installment: number;
  interest: number;
  principal: number;
  balance: number;
}

export interface SimulationResult {
  price: number;
  down: number;
  principal: number;
  term: number;
  tna: number;
  firstInstallment: number;
  lastInstallment: number;
  totalInterest: number;
  insuranceTotal: number;
  adminFees: number;
  totalFinancedPaid: number;
  totalCost: number;
  schedule: ScheduleRow[];
}

/** La configuración propia de la unidad (promoción) tiene prioridad sobre la de la marca. */
export function getEffectiveOffer(vehicle: Vehicle, brands: Brand[]): OfferContext | null {
  if (vehicle.offer) return { offer: vehicle.offer, source: 'unidad' };
  const circular = getCircularFor(vehicle);
  const best = circular ? bestCircularQuote(circular, vehicle) : null;
  if (best) return { offer: circularToOffer(best), source: 'marca', circular: best };
  const brand = brands.find((b) => b.id === vehicle.brandId);
  return brand ? { offer: brand.defaultOffer, source: 'marca' } : null;
}

/** Una oferta sólo se publica como tal si fue verificada y está vigente. */
export function isOfferPublishable(offer: FinanceOffer): boolean {
  return offer.verified && !isExpired(offer.validUntil);
}

export function getDownBounds(price: number, offer: FinanceOffer): { min: number; max: number } {
  const byPct = Math.ceil((price * offer.minDownPct) / 100);
  const byCap = offer.maxFinanceAmount ? Math.max(0, price - offer.maxFinanceAmount) : 0;
  return { min: Math.min(price, Math.max(byPct, byCap)), max: price };
}

export function simulate(price: number, down: number, term: number, offer: FinanceOffer): SimulationResult | null {
  if (offer.tna === null || term <= 0) return null;
  const principal = Math.max(0, price - down);
  const i = offer.tna / 100 / 12;
  const schedule: ScheduleRow[] = [];
  let balance = principal;

  if (offer.amortization === 'frances') {
    const installment = i === 0 ? principal / term : (principal * i) / (1 - Math.pow(1 + i, -term));
    for (let n = 1; n <= term; n++) {
      const interest = balance * i;
      const amort = installment - interest;
      balance = Math.max(0, balance - amort);
      schedule.push({ n, installment, interest, principal: amort, balance });
    }
  } else {
    const amort = principal / term;
    for (let n = 1; n <= term; n++) {
      const interest = balance * i;
      balance = Math.max(0, balance - amort);
      schedule.push({ n, installment: amort + interest, interest, principal: amort, balance });
    }
  }

  const totalInterest = schedule.reduce((s, r) => s + r.interest, 0);
  const insuranceTotal = (offer.monthlyInsurance ?? 0) * term;
  const adminFees = offer.adminFees ?? 0;
  const totalFinancedPaid = principal + totalInterest + insuranceTotal + adminFees;

  return {
    price,
    down,
    principal,
    term,
    tna: offer.tna,
    firstInstallment: schedule[0]?.installment ?? 0,
    lastInstallment: schedule[schedule.length - 1]?.installment ?? 0,
    totalInterest,
    insuranceTotal,
    adminFees,
    totalFinancedPaid,
    totalCost: down + totalFinancedPaid,
    schedule,
  };
}

export interface QuickQuote {
  ctx: OfferContext;
  down: number;
  principal: number;
  term: number;
  sim: SimulationResult | null;
}

/** Cuota de referencia: anticipo mínimo y plazo más largo habilitado para la unidad. */
export function getQuickQuote(vehicle: Vehicle, brands: Brand[]): QuickQuote | null {
  const ctx = getEffectiveOffer(vehicle, brands);
  if (!ctx || ctx.offer.terms.length === 0) return null;
  if (ctx.circular) {
    const q = ctx.circular;
    return { ctx, down: q.down, principal: q.financed, term: q.row.term, sim: circularToSimulation(q) };
  }
  const { min } = getDownBounds(vehicle.price, ctx.offer);
  const term = Math.max(...ctx.offer.terms);
  return { ctx, down: min, principal: vehicle.price - min, term, sim: simulate(vehicle.price, min, term, ctx.offer) };
}

/**
 * Convierte una cotización de circular (cuota por coeficiente cada $1.000) en el formato de simulación.
 * El cuadro reparte el capital en partes iguales; el resto de cada cuota corresponde a intereses e IVA.
 */
export function circularToSimulation(q: CircularQuote): SimulationResult {
  const amort = q.financed / q.row.term;
  const schedule: ScheduleRow[] = [];
  let balance = q.financed;
  for (let n = 1; n <= q.row.term; n++) {
    balance = Math.max(0, balance - amort);
    schedule.push({ n, installment: q.installment, interest: Math.max(0, q.installment - amort), principal: amort, balance });
  }
  const totalInterest = Math.max(0, q.totalInstallments - q.financed);
  return {
    price: q.price,
    down: q.down,
    principal: q.financed,
    term: q.row.term,
    tna: q.row.tna,
    firstInstallment: q.installment,
    lastInstallment: q.installment,
    totalInterest,
    insuranceTotal: 0,
    adminFees: q.managementFee,
    totalFinancedPaid: q.totalInstallments + q.managementFee,
    totalCost: q.totalCost,
    schedule,
  };
}

export const amortizationLabel = (a: FinanceOffer['amortization']) =>
  a === 'frances' ? 'Francés (cuota fija)' : 'Alemán (cuota decreciente)';
